import { ReactNode, useRef, useState } from "react";
import { Box, Typography, SxProps, Theme } from "@mui/material";
import { motion, useMotionValueEvent, useScroll } from "framer-motion";

export interface StickyScrollContent {
  title: string;
  description: string;
  content: ReactNode;
}

interface StickyScrollProps {
  content: StickyScrollContent[];
}

const StickyScroll = ({ content }: StickyScrollProps) => {
  const [activeCard, setActiveCard] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const cardLength = content.length;

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    // Calculate which card should be active based on scroll progress
    // latest goes from 0 to 1 as we scroll through the container
    // We need to map this to card indices (0 to cardLength-1)
    const rawIndex = latest * cardLength;
    const cardIndex = Math.min(Math.max(Math.floor(rawIndex), 0), cardLength - 1);
    setActiveCard(cardIndex);
  });

  const backgroundColors = [
    "linear-gradient(to bottom right, #06b6d4, #10b981)",
    "linear-gradient(to bottom right, #8b5cf6, #ec4899)",
    "linear-gradient(to bottom right, #f59e0b, #ef4444)",
    "linear-gradient(to bottom right, #3b82f6, #6366f1)",
  ];

  return (
    <Box sx={styles.container} ref={containerRef}>
      <Box
        sx={{
          ...styles.contentWrapper,
          minHeight: { md: `${content.length * 100}vh` },
        }}
      >
        <Box sx={styles.textSection}>
          {content.map((item, index) => {
            const isActive = activeCard === index;
            const distance = Math.abs(activeCard - index);

            return (
              <motion.div
                key={`text-${index}`}
                initial={false}
                animate={{
                  opacity: isActive ? 1 : Math.max(0.2, 0.5 - distance * 0.15),
                  y: isActive ? 0 : distance * 30,
                }}
                transition={{
                  duration: 0.6,
                  ease: [0.16, 1, 0.3, 1],
                }}
                style={styles.textItem}
              >
                <motion.div
                  animate={{
                    opacity: isActive ? 1 : 0.4,
                  }}
                  transition={{ duration: 0.4 }}
                >
                  <Typography variant="h2" component="h2" sx={styles.title(isActive)}>
                    {item.title}
                  </Typography>
                  <Typography variant="body1" component="p" sx={styles.description(isActive)}>
                    {item.description}
                  </Typography>
                </motion.div>
              </motion.div>
            );
          })}
        </Box>
        <Box sx={styles.stickySection}>
          {content.map((item, index) => {
            const isActive = activeCard === index;
            const distance = Math.abs(activeCard - index);

            return (
              <motion.div
                key={`content-${index}`}
                initial={false}
                animate={{
                  opacity: isActive ? 1 : 0,
                }}
                transition={{
                  duration: 0.5,
                  ease: [0.16, 1, 0.3, 1],
                }}
                style={{
                  ...styles.stickyCard,
                  zIndex: isActive ? 10 : Math.max(1, 5 - distance),
                  filter: isActive ? "blur(0px)" : `blur(${Math.min(12, distance * 4)}px)`,
                }}
              >
                <Box
                  sx={{
                    ...styles.cardContent,
                    background: backgroundColors[index % backgroundColors.length],
                  }}
                >
                  {item.content}
                </Box>
              </motion.div>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default StickyScroll;

const styles = {
  container: {
    position: "relative" as const,
    width: "100%",
  },
  contentWrapper: {
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    position: "relative",
  },
  textSection: {
    display: "flex",
    flexDirection: "column",
    width: { xs: "100%", md: "50%" },
    position: "relative" as const,
  },
  textItem: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    width: "100%",
    minHeight: "100vh",
    paddingTop: "2rem",
    paddingBottom: "2rem",
    paddingLeft: "2rem",
    paddingRight: "2rem",
  } as React.CSSProperties,
  title: (isActive: boolean): SxProps<Theme> => ({
    fontSize: { xs: "2rem", md: "3rem", lg: "4rem" },
    fontWeight: 700,
    lineHeight: 1.2,
    color: "#fff",
    mb: 2,
    transition: "opacity 0.3s ease",
  }),
  description: (isActive: boolean): SxProps<Theme> => ({
    fontSize: { xs: "1rem", md: "1.125rem", lg: "1.25rem" },
    color: "rgba(255, 255, 255, 0.8)",
    lineHeight: 1.6,
    transition: "opacity 0.3s ease",
  }),
  stickySection: {
    display: "flex",
    flexDirection: "column",
    width: { xs: "100%", md: "50%" },
    position: { md: "sticky" as const },
    top: 0,
    height: { xs: "auto", md: "100vh" },
    overflow: "hidden",
  },
  stickyCard: {
    position: "absolute" as const,
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  cardContent: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    borderRadius: { xs: 0, md: 2 },
    mx: { xs: 0, md: 4 },
    my: { xs: 2, md: 4 },
  },
};
